import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ShieldCheck, ShieldAlert, Save, FileSignature, Settings2, BadgeCheck, KeyRound, Users2, Trash2 } from 'lucide-react';
import { PersonAvatar, Tabs, useUrlTab } from '../../components/ui';
import { useConfirm } from '../../components/ConfirmDialog';
import { ScreeningRegister, GovAccess } from './ScreeningAccess';
import {
  PageHead, FigureStrip, Figure, Register, DetailDrawer, AcStatus, Panel, Field, FormRow, ErrorState, fmtDate, Deadline,
} from './design';
import { WorkforceStreams } from './Connections';

const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const METHOD = { digital: 'Digital', paper: 'Paper, signed', verbal: 'Verbal' };

// The page was five panels stacked one under another; it is split into the four jobs people come
// here for, and the open one lives in the address (?view=) like the CHSP screen's sections.
const VIEWS = ['screening', 'coc', 'access', 'awards'];

// Code of Conduct standing, expressed in the shared status words so the badge means the same
// thing here as on every other aged care screen.
function cocStatus(w, current) {
  if (!w.coc_ack_version && !w.coc_ack_at) return { status: 'overdue', label: 'Not acknowledged' };
  if (current && w.coc_ack_version && w.coc_ack_version !== current) return { status: 'expiring', label: `Outdated (v${w.coc_ack_version})` };
  return { status: 'acknowledged', label: 'Acknowledged' };
}

function ChecksCell({ w }) {
  if (!w.required_checks) return <span className="text-slate-500">No requirements for this role</span>;
  if (w.lapsed_checks > 0) return <span className="text-red-800 font-medium">{w.lapsed_checks} of {w.required_checks} lapsed or missing</span>;
  return <span className="text-slate-600">All {w.required_checks} current</span>;
}

function WorkerCell({ w }) {
  return (
    <span className="flex items-center gap-2.5 min-w-0">
      <PersonAvatar id={w.id} name={w.name} photo={w.profile_photo} size="w-7 h-7 text-[10px]" />
      <span className="truncate font-medium text-slate-800">{w.name}</span>
    </span>
  );
}

// Every acknowledgement recorded for one worker, newest first. One recorded against the wrong
// worker, or twice, can be removed; the worker's standing is worked out again from what is left.
function CocHistory({ worker, reloadKey, onChanged }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/compliance/coc-ack', { params: { staff_id: worker.id } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : (r.data?.acks || [])))
      .catch((e) => { setRows(null); setError(e.response?.data?.message || 'Could not load the past acknowledgements'); });
  }, [worker.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const remove = async (a) => {
    const what = `${a.acknowledged_at ? fmtDate(a.acknowledged_at) : 'undated'}${a.version ? `, version ${a.version}` : ''}`;
    const ok = await confirm(`The acknowledgement (${what}) is removed from ${worker.name}'s record. Use this for one recorded by mistake.`, { title: 'Remove this acknowledgement?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/compliance/coc-ack/${a.id}`); toast.success('Acknowledgement removed'); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove the acknowledgement'); }
  };
  return (
    <div>
      <p className="text-[13px] font-medium text-slate-800 mb-1.5">Past acknowledgements</p>
      {error ? (
        <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-red-800" role="alert">
          {error}
          <button type="button" className="btn-secondary btn-sm" onClick={load}>Try again</button>
        </p>
      ) : rows == null ? (
        <p className="text-[12.5px] text-slate-500" aria-busy="true">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-[12.5px] text-slate-500">None recorded.</p>
      ) : (
        <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {rows.map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-2 px-3 py-2 text-[12.5px]">
              <span className="min-w-0">
                <span className="block text-slate-800 tabular-nums">{a.acknowledged_at ? fmtDate(a.acknowledged_at) : 'No date'}{a.version ? `, version ${a.version}` : ', no version'}</span>
                <span className="block text-slate-500">{METHOD[a.method] || cap(a.method) || 'Method not recorded'}{a.notes ? ` · ${a.notes}` : ''}</span>
              </span>
              <button type="button" className="btn-ghost btn-sm hover:text-red-700 shrink-0" onClick={() => remove(a)}><Trash2 size={13} /> Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AgedCareWorkerCompliance() {
  const [view, setView] = useUrlTab('view', 'screening', VIEWS);
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [workersError, setWorkersError] = useState(null);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  // The worker drawer: a view of their Code of Conduct and checks, which turns into the "record an
  // acknowledgement" form only when asked. Opening a row used to go straight to the form.
  const [drawer, setDrawer] = useState(null); // { id, mode: 'view' | 'ack', back }
  const [ackForm, setAckForm] = useState({});
  const [savingAck, setSavingAck] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  // Stable, or the drawer re-runs its focus effect on every keystroke in the form and pulls focus
  // out of the field being typed in.
  const closeDrawer = useCallback(() => setDrawer(null), []);

  const loadWorkers = useCallback(async () => {
    setWorkersError(null);
    try { const r = await api.get('/aged-care/compliance/workers'); setWorkers(r.data?.workers || []); setCurrentVersion(r.data?.current_version || null); }
    catch (e) { setWorkers([]); setWorkersError(e.response?.data?.message || 'Could not load the care workers'); }
  }, []);
  const loadSettings = useCallback(async () => {
    setSettingsError(null);
    try { const s = await api.get('/aged-care/compliance/settings'); setSettings(s.data || {}); }
    catch (e) { setSettings(null); setSettingsError(e.response?.data?.message || 'Could not load the Code of Conduct settings'); }
  }, []);
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSettings(), loadWorkers()]);
      setLoading(false);
    })();
  }, [loadSettings, loadWorkers]);
  const retryWorkers = async () => { setWorkersLoading(true); await loadWorkers(); setWorkersLoading(false); };

  // Sends only what this panel owns. The settings row also holds the screening policy (whether a
  // worker may start while a check is pending), which is changed on the Screening tab; sending the
  // copy loaded when this page opened put back whatever it was then and undid a change made there.
  const saveCfg = async () => {
    if (!settings) return;
    setSavingCfg(true);
    const body = {
      block_roster_on_lapsed: !!settings.block_roster_on_lapsed,
      coc_version: settings.coc_version || null,
      coc_effective_from: settings.coc_effective_from ? String(settings.coc_effective_from).slice(0, 10) : null,
      coc_url: settings.coc_url || null,
      coc_content: settings.coc_content || null,
    };
    try { const r = await api.put('/aged-care/compliance/settings', body); setSettings(r.data); toast.success('Settings saved'); loadWorkers(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the settings'); } finally { setSavingCfg(false); }
  };
  const openView = (w) => setDrawer({ id: w.id, mode: 'view' });
  const openAck = (w, back = false) => { setAckForm({ acknowledged_at: isoToday(), method: 'digital', notes: '' }); setDrawer({ id: w.id, mode: 'ack', back }); };
  const worker = drawer ? workers.find((w) => w.id === drawer.id) || null : null;
  const submitAck = async () => {
    if (!worker) return;
    setSavingAck(true);
    try {
      await api.post('/aged-care/compliance/coc-ack', { staff_id: worker.id, version: currentVersion || null, ...ackForm });
      toast.success(`Acknowledgement recorded for ${worker.name}`);
      setDrawer({ id: worker.id, mode: 'view' }); setHistoryKey((k) => k + 1); loadWorkers();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record the acknowledgement'); } finally { setSavingAck(false); }
  };

  // Mirror cocStatus(): an ack counts as current when its version matches (or no version is configured).
  const ackedCurrent = workers.filter((w) => (w.coc_ack_version || w.coc_ack_at) && (!currentVersion || w.coc_ack_version === currentVersion)).length;
  const lapsed = workers.filter((w) => w.lapsed_checks > 0).length;
  const blockOn = !!settings?.block_roster_on_lapsed;
  const setS = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  const head = (
    <PageHead icon={ShieldCheck}
      title="Worker compliance"
      purpose="Whose mandatory checks are current, who has acknowledged the Aged Care Code of Conduct, and who still has access to the government systems. Expiry dates live on each worker's Staff documents page; this screen reads them and can stop a lapsed worker being rostered."
    />
  );

  if (loading) {
    return (
      <div>
        {head}
        <div className="space-y-5" aria-busy="true" aria-label="Loading">
          <div className="skeleton h-20 rounded-xl" />
          <div className="skeleton h-10 rounded-xl" />
          <div className="skeleton h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  const roleCol = { key: 'role', header: 'Role', width: 140, sortable: true, render: (w) => <span className="text-slate-600">{cap(w.role)}</span> };
  const screeningCols = [
    { key: 'name', header: 'Worker', truncate: true, sortable: true, render: (w) => <WorkerCell w={w} /> },
    roleCol,
    { key: 'lapsed_checks', header: 'Mandatory checks', width: 220, sortable: true, defaultDir: 'desc', render: (w) => <ChecksCell w={w} /> },
    // A lapsed check stops this worker being rostered, so the date it happens is what the
    // coordinator acts on — a count alone left them hunting for which check and when.
    {
      key: 'next_expiry', header: 'Next expiry', width: 210, sortable: true,
      sortValue: (w) => w.next_expiry || '9999-12-31',
      render: (w) => (w.next_expiry
        ? (
          <span className="min-w-0 block">
            <Deadline date={w.next_expiry} absolute={false} />
            {w.next_expiry_name && <span className="block text-[12px] text-slate-500 truncate">{w.next_expiry_name}</span>}
          </span>
        )
        : <span className="text-slate-300">—</span>),
    },
  ];
  const cocCols = [
    { key: 'name', header: 'Worker', truncate: true, sortable: true, render: (w) => <WorkerCell w={w} /> },
    roleCol,
    { key: 'coc', header: 'Code of Conduct', width: 200, sortable: true, sortValue: (w) => cocStatus(w, currentVersion).status, render: (w) => {
      const cs = cocStatus(w, currentVersion);
      return <AcStatus status={cs.status} label={cs.label} />;
    } },
    { key: 'coc_ack_at', header: 'Last acknowledged', width: 190, sortable: true, render: (w) => (w.coc_ack_at
      ? <span className="tabular-nums text-slate-600">{fmtDate(w.coc_ack_at)}{w.coc_ack_version ? <span className="text-slate-500">, v{w.coc_ack_version}</span> : null}</span>
      : <span className="text-slate-300">—</span>) },
  ];

  // Rose when a check has lapsed (the roster block bites here); amber when the Code of Conduct
  // acknowledgement is missing or for an older version.
  const screeningTone = (w) => (w.lapsed_checks > 0 ? 'rose' : undefined);
  const cocTone = (w) => (cocStatus(w, currentVersion).status === 'acknowledged' ? undefined : 'amber');
  const emptyWorkers = {
    icon: ShieldCheck,
    title: 'No care workers found',
    hint: 'Active staff with the carer, support worker or staff role appear here. Add them under Staff first.',
  };

  const standing = worker ? cocStatus(worker, currentVersion) : null;

  return (
    <div>
      {head}

      <FigureStrip>
        <Figure label="Acknowledged the current Code of Conduct" value={workersError ? '—' : `${ackedCurrent} of ${workers.length}`} />
        <Figure label="Workers with a lapsed check" value={workersError ? '—' : lapsed} tone={lapsed ? 'red' : 'muted'} />
        <Figure label="Roster block on lapsed checks" value={settings ? (blockOn ? 'On' : 'Off') : '—'} tone={blockOn ? 'slate' : 'muted'} />
        <Figure label="Care workers" value={workersError ? '—' : workers.length} />
      </FigureStrip>

      <Tabs className="mb-5" value={view} onChange={setView} ariaLabel="Worker compliance sections"
        tabs={[
          { id: 'screening', label: 'Screening and checks', icon: BadgeCheck },
          { id: 'coc', label: 'Code of Conduct', icon: FileSignature },
          { id: 'access', label: 'Government system access', icon: KeyRound },
          { id: 'awards', label: 'Awards and streams', icon: Users2 },
        ]}
      />

      {view === 'screening' && (
        <>
          <Register
            title="Care workers" count={workers.length} countLabel={workers.length === 1 ? 'worker' : 'workers'}
            columns={screeningCols} rows={workers} rowKey="id" loading={workersLoading} error={workersError} onRetry={retryWorkers}
            onRowClick={openView} activeRowKey={drawer ? drawer.id : null} rowTone={screeningTone}
            empty={emptyWorkers}
            footer={settings ? (blockOn
              ? 'A worker with a lapsed or missing mandatory check cannot be rostered. The roster block is set on the Code of Conduct tab.'
              : 'Workers with a lapsed or missing mandatory check can still be rostered. The roster block is set on the Code of Conduct tab.') : undefined}
          />
          <ScreeningRegister />
        </>
      )}

      {view === 'coc' && (
        <>
          <Panel
            title="Aged Care Code of Conduct and roster enforcement" icon={Settings2} className="mb-5" bodyClass={settingsError ? undefined : 'p-4 space-y-4'}
            actions={settings && <button type="button" onClick={saveCfg} disabled={savingCfg} className="btn-primary btn-sm"><Save size={14} /> {savingCfg ? 'Saving…' : 'Save settings'}</button>}
          >
            {settingsError ? <ErrorState message={settingsError} onRetry={loadSettings} /> : (
              <>
                <FormRow cols={3}>
                  <Field label="Current version" hint="Workers who acknowledged an earlier version show as outdated.">
                    {(id) => <input id={id} className="input-field" value={settings?.coc_version || ''} onChange={(e) => setS('coc_version', e.target.value)} placeholder="2025.1" />}
                  </Field>
                  <Field label="Effective from">
                    {(id) => <input id={id} type="date" className="input-field" value={settings?.coc_effective_from ? String(settings.coc_effective_from).slice(0, 10) : ''} onChange={(e) => setS('coc_effective_from', e.target.value)} />}
                  </Field>
                  <Field label="Document link" hint="Optional.">
                    {(id) => <input id={id} className="input-field" value={settings?.coc_url || ''} onChange={(e) => setS('coc_url', e.target.value)} placeholder="https://" />}
                  </Field>
                </FormRow>
                <Field label="Code of Conduct text" hint="Optional. The text workers are asked to acknowledge.">
                  {(id) => <textarea id={id} rows={3} className="input-field" value={settings?.coc_content || ''} onChange={(e) => setS('coc_content', e.target.value)} placeholder="Paste the Aged Care Code of Conduct workers must acknowledge" />}
                </Field>
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={blockOn} onChange={(e) => setS('block_roster_on_lapsed', e.target.checked)} />
                  <span className="text-[13px] text-slate-700">
                    <span className="font-medium">Block rostering when a mandatory check is lapsed or missing.</span>
                    <span className="block text-[12px] text-slate-500 mt-0.5">When this is on, a worker with an expired or missing role-required qualification cannot be assigned a shift: the scheduler refuses the booking rather than warning about it. Mandatory checks are defined under Settings, Compliance requirements. Off by default.</span>
                  </span>
                </label>
              </>
            )}
          </Panel>

          <Register
            title="Acknowledgements" count={workers.length} countLabel={workers.length === 1 ? 'worker' : 'workers'}
            columns={cocCols} rows={workers} rowKey="id" loading={workersLoading} error={workersError} onRetry={retryWorkers}
            onRowClick={openView} activeRowKey={drawer ? drawer.id : null} rowTone={cocTone}
            rowActions={(w) => (
              <button type="button" onClick={(e) => { e.stopPropagation(); openAck(w); }} className="btn-ghost btn-sm">
                <FileSignature size={14} /> Record acknowledgement
              </button>
            )}
            empty={emptyWorkers}
          />
        </>
      )}

      {view === 'access' && <GovAccess className="" />}
      {view === 'awards' && <WorkforceStreams className="" />}

      {worker && (
        <DetailDrawer
          open onClose={closeDrawer} icon={drawer.mode === 'ack' ? FileSignature : ShieldCheck} width="sm"
          title={worker.name} subtitle={drawer.mode === 'ack' ? 'Record a Code of Conduct acknowledgement' : cap(worker.role)}
          statusNode={<AcStatus status={standing.status} label={standing.label} />}
          meta={[
            { label: 'Last acknowledged', value: worker.coc_ack_at ? `${fmtDate(worker.coc_ack_at)}${worker.coc_ack_version ? `, version ${worker.coc_ack_version}` : ''}` : 'Never' },
            { label: 'Current version', value: currentVersion || 'Not set' },
            { label: 'Mandatory checks', value: <ChecksCell w={worker} /> },
            drawer.mode === 'view' ? { label: 'Next expiry', value: worker.next_expiry ? <span>{fmtDate(worker.next_expiry)}{worker.next_expiry_name ? `, ${worker.next_expiry_name}` : ''}</span> : null } : null,
          ]}
          footer={drawer.mode === 'ack' ? (
            <>
              <button type="button" onClick={() => (drawer.back ? setDrawer({ id: worker.id, mode: 'view' }) : closeDrawer())} className="btn-secondary">Cancel</button>
              <button type="button" onClick={submitAck} disabled={savingAck} className="btn-primary"><Save size={15} /> {savingAck ? 'Saving…' : 'Record acknowledgement'}</button>
            </>
          ) : (
            <>
              <button type="button" onClick={closeDrawer} className="btn-secondary">Close</button>
              <button type="button" onClick={() => openAck(worker, true)} className="btn-primary"><FileSignature size={15} /> Record acknowledgement</button>
            </>
          )}
        >
          {drawer.mode === 'ack' ? (
            <div className="space-y-4">
              <p className="text-[13px] text-slate-600">
                Record that {worker.name} has acknowledged the Aged Care Code of Conduct{currentVersion ? `, version ${currentVersion}` : ''}. This is your record of the acknowledgement; keep the signed or digital copy with the worker's documents.
              </p>
              <FormRow cols={2}>
                <Field label="Acknowledged on">
                  {(id) => <input id={id} type="date" className="input-field" value={ackForm.acknowledged_at || ''} max={isoToday()} onChange={(e) => setAckForm((p) => ({ ...p, acknowledged_at: e.target.value }))} />}
                </Field>
                <Field label="Method">
                  {(id) => (
                    <select id={id} className="input-field" value={ackForm.method} onChange={(e) => setAckForm((p) => ({ ...p, method: e.target.value }))}>
                      <option value="digital">Digital</option>
                      <option value="paper">Paper, signed</option>
                      <option value="verbal">Verbal</option>
                    </select>
                  )}
                </Field>
              </FormRow>
              <Field label="Notes" hint="Optional.">
                {(id) => <textarea id={id} rows={2} className="input-field" value={ackForm.notes || ''} onChange={(e) => setAckForm((p) => ({ ...p, notes: e.target.value }))} />}
              </Field>
              {!currentVersion && (
                <p className="flex items-start gap-2 text-[12.5px] text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <ShieldAlert size={14} className="shrink-0 mt-0.5" />
                  <span>No Code of Conduct version is set (Code of Conduct tab, settings), so this acknowledgement will be recorded without one. Set the version first if you want it to count against a specific release.</span>
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[12.5px] text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                Mandatory checks and their expiry dates are kept on {worker.name}'s Staff documents page. This screen reads them.
              </p>
              <CocHistory worker={worker} reloadKey={historyKey} onChanged={loadWorkers} />
            </div>
          )}
        </DetailDrawer>
      )}
    </div>
  );
}
